import React, { createContext, useCallback, useEffect, useMemo, useContext } from 'react';
import usePatchGenericEvent from '@hooks/requests/generic-event.patch.hook';
import useGetGenericEvent from '@hooks/requests/generic-event.get.hook';
import useDeleteGenericEvent from '@hooks/requests/generic-event.delete.hook';
import useGetTimeZones from '@hooks/requests/time-zones.get.hook';
import useGreedyState from '@hooks/use-greedy-state.hook';
import { dateClient } from '@utils/date-client';
import { TimeZoneContext } from '@contexts/timezone-context';
export const EditGenericEventModalContext = createContext(undefined);
export function EditGenericEventModalContextProvider(params) {
    const { timeZonesMapping } = useContext(TimeZoneContext);
    const { save: saveGenericEvent, saving, result: savedGenericEvents } = usePatchGenericEvent();
    const { deleteGenericEvent, result: deletedGenericEvents } = useDeleteGenericEvent(params.id);
    const { data: fetchedGenericEvent, loading: genericEventLoading, error: genericEventError } = useGetGenericEvent(params.id);
    const { data: genericEvent, setData: setGenericEvent } = useGreedyState(fetchedGenericEvent);
    const { timeZonesParams } = useMemo(() => {
        let timeZoneParams;
        if (!genericEvent)
            return { timeZonesParams: timeZoneParams };
        timeZoneParams = {
            select: 'all',
            filter: {
                resourceType: 'GenericEvent',
                resourceId: genericEvent.id
            }
        };
        return { timeZonesParams: timeZoneParams };
    }, [genericEvent]);
    // Load Timezones
    const { data: timeZoneList, loading: timeZonesLoading, error: timeZonesError } = useGetTimeZones(timeZonesParams);
    const modalDataInitializing = genericEventLoading || timeZonesLoading;
    const modalInitializationError = genericEventError || timeZonesError;
    const closeModal = useCallback(() => {
        params.onClose();
    }, []);
    const handleModalSave = useCallback((modalParams) => {
        const start = dateClient(modalParams.genericEvent.start).tz(timeZonesMapping[modalParams.genericEvent.timeZone], true);
        const end = dateClient(modalParams.genericEvent.end).tz(timeZonesMapping[modalParams.genericEvent.timeZone], true);
        const originalStart = modalParams.originalStart &&
            dateClient(modalParams.originalStart).tz(timeZonesMapping[modalParams.genericEvent.timeZone], true);
        return saveGenericEvent({
            genericEvent: Object.assign(Object.assign({}, modalParams.genericEvent), { start,
                end }),
            updateFutureEvents: modalParams.updateFutureEvents,
            originalStart
        });
    }, [timeZonesMapping, saveGenericEvent]);
    useEffect(() => {
        if (savedGenericEvents) {
            params.onSave(savedGenericEvents);
        }
    }, [savedGenericEvents]);
    useEffect(() => {
        if (deletedGenericEvents) {
            setGenericEvent(undefined);
            params.onDelete(deletedGenericEvents);
        }
    }, [deletedGenericEvents]);
    const contextData = useMemo(() => {
        const value = {
            modalReady: !modalDataInitializing,
            modalError: modalInitializationError !== null && modalInitializationError !== void 0 ? modalInitializationError : null,
            genericEvent: genericEvent !== null && genericEvent !== void 0 ? genericEvent : null,
            saving,
            timeZoneList: timeZoneList !== null && timeZoneList !== void 0 ? timeZoneList : [],
            closeModal,
            saveGenericEvent,
            handleModalSave,
            deleteGenericEvent
        };
        return value;
    }, [genericEvent, savedGenericEvents, timeZoneList, saving, modalDataInitializing, modalInitializationError]);
    return (React.createElement(EditGenericEventModalContext.Provider, { value: contextData }, params.children));
}
